import { useCallback, useRef, useState } from "react";
import {
  useResetAttempt,
  type TrainingAttempt,
} from "@/lib/api/training-simulator/training-simulator.ts";
import { useToast } from "@/components/ui/use-toast";

interface UseTrainingResumePromptParams {
  attemptId: string;
  attempt: TrainingAttempt | undefined;
  mentorMessageCount: number;
  transcriptLoaded: boolean;
  isCompleted: boolean;
  /** True while an intro modal (welcome/context) is taking precedence. */
  isSuppressed: boolean;
}

type ResumePhase = "prompt" | "starting-over" | "done";

export interface TrainingResumePrompt {
  open: boolean;
  levelPosition: number;
  currentMessage: number;
  totalMessages: number;
  isStartingOver: boolean;
  onResume: () => void;
  onStartOver: () => void;
}

export interface TrainingResumePromptState {
  modal: TrainingResumePrompt;
  /**
   * True once the prompt will no longer block the session — either the attempt
   * wasn't a resume or the mentor has chosen. The session gates the level
   * context modal on this so the resume prompt always comes first.
   */
  isResolved: boolean;
}

/**
 * Drives the "Welcome back" prompt shown when a mentor re-enters an in-progress
 * attempt that already has messages, owning the resume/start-over choice and the
 * reset action so the session component stays presentational.
 */
export function useTrainingResumePrompt({
  attemptId,
  attempt,
  mentorMessageCount,
  transcriptLoaded,
  isCompleted,
  isSuppressed,
}: UseTrainingResumePromptParams): TrainingResumePromptState {
  const { mutateAsync: resetAttempt } = useResetAttempt(attemptId);
  const { toast } = useToast();

  // One-time snapshot, taken once both attempt and transcript have loaded, of
  // whether we arrived on an attempt with existing progress. Latching it keeps
  // the prompt from re-firing after the mentor sends their first message.
  const resumedWithProgressRef = useRef<boolean | null>(null);

  if (resumedWithProgressRef.current === null && transcriptLoaded && attempt) {
    resumedWithProgressRef.current =
      attempt.status === "in_progress" && mentorMessageCount > 0;
  }

  const [phase, setPhase] = useState<ResumePhase>("prompt");

  const onResume = useCallback(() => setPhase("done"), []);

  const onStartOver = useCallback(async () => {
    setPhase("starting-over");
    try {
      await resetAttempt();
      setPhase("done");
    } catch {
      setPhase("prompt");
      toast({
        title: "Couldn't start the level over",
        description: "Something went wrong. Please try again.",
        variant: "destructive",
      });
    }
  }, [resetAttempt, toast]);

  return {
    modal: {
      open:
        resumedWithProgressRef.current === true &&
        phase !== "done" &&
        !isSuppressed &&
        !isCompleted,
      levelPosition: attempt?.level.position ?? 0,
      currentMessage: mentorMessageCount,
      totalMessages: attempt?.level.completion_policy.target ?? 0,
      isStartingOver: phase === "starting-over",
      onResume,
      onStartOver,
    },
    isResolved: resumedWithProgressRef.current === false || phase === "done",
  };
}
